import { FunctionComponent, useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReferrerQueryString } from '@fff-web/fff-analytics';
import { usePriceLockEligibility, useUserContext } from '@fff-web/fff-utilities';
import { LOCK_IN_RATE_PATH } from '../../../../utils/routePaths';
import {
  BOX_STATUS,
  CANCEL_BOX_SUBSCRIPTION_COMPLETED_EVENT_NAME,
  CANCEL_BOX_SUBSCRIPTION_LABEL,
  CANCEL_BOX_SUBSCRIPTION_STARTED_EVENT_NAME,
  CANCEL_SUBSCRIPTION_LABEL,
  Plans,
  REACTIVATE_MEMBERSHIP_LABEL,
  SUBSCRIPTION_STATUS,
} from '../../../constants';
import { ClubMembershipModal } from '../../ClubMembershipModal';
import { SubscriptionStatus } from '../../MyAccountPage';
import { getBoxStatus } from '../../services/api/box';
import ManageMembershipLink from './ManageMembershipLink';
import {
  trackClickCancelSubscription,
  trackClickReactivateMembership,
  trackClickSwapForCredit,
} from './trackEvents';
import * as manageMembershipStyles from './ManageMembership.module.css';

const REFERRER_QUERY_STRING = `?referrer=${ReferrerQueryString.myAccount}`;

type ManageMembershipProps = {
  isClubMember: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
  boxSubscriptionId?: string | null;
};

const ManageMembership: FunctionComponent<ManageMembershipProps> = ({
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
  boxSubscriptionId,
}) => {
  const [isCancelMembershipModalOpen, setIsCancelMembershipModalOpen] = useState(false);
  const [isCancelBoxModalOpen, setIsCancelBoxModalOpen] = useState(false);
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [boxStatus, setBoxStatus] = useState('');

  const { priceLock: priceLockFF } = useFlags();
  const { userInfo } = useUserContext();
  const { isEligible: isPriceLockEligible } = usePriceLockEligibility({
    priceLockFF,
    enabled: !isClubMember,
  });
  const { user } = userInfo;
  const { planCode } = user;

  const displaySwapForCredit =
    planCode === Plans.TRIAL
      ? subscriptionState !== SUBSCRIPTION_STATUS.ACTIVE
      : boxStatus !== BOX_STATUS.SKIPPED_INTRO;

  const isCanceled = subscriptionState === SUBSCRIPTION_STATUS.CANCELED;
  const isExpired = subscriptionState === SUBSCRIPTION_STATUS.EXPIRED;

  const clickSwapForCredit = () => {
    trackClickSwapForCredit(userInfo);
  };

  const handleCancelSubscription = () => {
    trackClickCancelSubscription(user);
  };

  useEffect(() => {
    if (isClubMember) return;

    const fetchBoxStatus = async () => {
      const { boxState } = await getBoxStatus();
      setBoxStatus(boxState);
    };

    fetchBoxStatus();
  }, [isClubMember]);

  const handleClickCancelBoxSubscriptionCTA = () => {
    trackClickCancelSubscription(
      user,
      CANCEL_BOX_SUBSCRIPTION_LABEL,
      CANCEL_BOX_SUBSCRIPTION_STARTED_EVENT_NAME,
      'Beauty Box', //TODO: This might be dynamic or different eventually.
    );
    setIsCancelBoxModalOpen(true);
  };

  const getLinks = () => {
    if (isClubMember) {
      if (isCanceled || isExpired) {
        return (
          <ManageMembershipLink
            onClickCTA={() => setIsReactivateModalOpen(true)}
            title="Reactivate Membership"
            subtitle="Reactivate today and gain instant access to all of the FabFitFun perks."
            isButton
          />
        );
      }
      return (
        <>
          {!boxSubscriptionId && (
            <ManageMembershipLink
              onClickCTA={() => setIsCancelMembershipModalOpen(true)}
              title="Cancel Membership"
              subtitle="By canceling your membership you will lose access to your FabFitFun benefits."
              isButton
            />
          )}
          {boxSubscriptionId && (
            <ManageMembershipLink
              onClickCTA={() => setIsCancelBoxModalOpen(true)}
              title={CANCEL_BOX_SUBSCRIPTION_LABEL}
              subtitle="By canceling your box subscription you will lose access to your FabFitFun benefits."
              isButton
            />
          )}
        </>
      );
    }

    if (isExpired) {
      return (
        <ManageMembershipLink
          path={`/reactivate-my-membership${REFERRER_QUERY_STRING}`}
          onClickCTA={trackClickReactivateMembership}
          title={REACTIVATE_MEMBERSHIP_LABEL}
          subtitle="Reactivate to a seasonal or annual membership today and gain
          instant access to all of the FabFitFun perks."
        />
      );
    }

    return (
      <>
        {isPriceLockEligible && (
          <ManageMembershipLink
            path={LOCK_IN_RATE_PATH}
            title="Lock in Your Rate"
            subtitle="Limited time offer. Lock in your current
              price before the membership price increases."
          />
        )}
        {displaySwapForCredit && (
          <ManageMembershipLink
            path={`/swap-for-credit${REFERRER_QUERY_STRING}`}
            onClickCTA={clickSwapForCredit}
            title="Swap for credit"
            subtitle="Swap your
              box for e-commerce credit and create your own shopping experience – so you
              decide how to shop with us!"
          />
        )}
        {!isCanceled ? (
          <ManageMembershipLink
            path={`/cancel-subscription${REFERRER_QUERY_STRING}`}
            title={CANCEL_SUBSCRIPTION_LABEL}
            subtitle="By ending your account you will
                  lose access to your FabFitFun benefits."
          />
        ) : (
          <ManageMembershipLink
            path={`/reactivate-today${REFERRER_QUERY_STRING}`}
            onClickCTA={handleCancelSubscription}
            title={REACTIVATE_MEMBERSHIP_LABEL}
            subtitle="Reactivate to a seasonal or annual membership today and gain
                instant access to all of the FabFitFun perks."
          />
        )}
        {boxSubscriptionId && (
          <ManageMembershipLink
            onClickCTA={handleClickCancelBoxSubscriptionCTA}
            title={CANCEL_BOX_SUBSCRIPTION_LABEL}
            subtitle="By canceling your box subscription you will lose access to your FabFitFun benefits."
            isButton
          />
        )}
      </>
    );
  };

  const onSubscriptionChangeDone = () => {
    refetchClubMemberSubscription();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onBoxSubscriptionChangeDone = () => {
    trackClickCancelSubscription(
      user,
      CANCEL_BOX_SUBSCRIPTION_LABEL,
      CANCEL_BOX_SUBSCRIPTION_COMPLETED_EVENT_NAME,
      'Beauty Box', //TODO: This might be dynamic or different eventually.
    );
    onSubscriptionChangeDone();
  };

  return (
    <div className={`${manageMembershipStyles.container}`}>
      {getLinks()}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isCancelMembershipModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsCancelMembershipModalOpen(false)}
          variant="cancel"
          onDone={onSubscriptionChangeDone}
        />
      )}
      {boxSubscriptionId && (
        <ClubMembershipModal
          isOpen={isCancelBoxModalOpen}
          subscriptionId={boxSubscriptionId}
          onClose={() => setIsCancelBoxModalOpen(false)}
          variant="cancel"
          onDone={onBoxSubscriptionChangeDone}
          isBeautyEditBoxSubscription
        />
      )}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isReactivateModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsReactivateModalOpen(false)}
          variant="reactivate"
          onDone={onSubscriptionChangeDone}
        />
      )}
    </div>
  );
};

export default ManageMembership;
